import { useEffect, useState } from 'react';
import { bobcatApiUrl } from '../../utils/bobcat-api';

/** What `/ai/health` says this deployment can do (see `AiHealthResponse`). */
export interface HealthCapabilities {
  engineTrace: boolean;
  voiceCalls: boolean;
}

const NONE: HealthCapabilities = { engineTrace: false, voiceCalls: false };

/** Only an explicit `true` counts: a missing field or a bad body is "no". */
export const readHealthCapabilities = (body: unknown): HealthCapabilities => {
  const caps = (body as { capabilities?: Record<string, unknown> } | null)
    ?.capabilities;
  return {
    engineTrace: caps?.engineTrace === true,
    voiceCalls: caps?.voiceCalls === true,
  };
};

/**
 * One shared request, so both capabilities come from one answer. Resolves
 * `null` when the answer could not be had (network error, non-2xx such as a
 * 401 from the session gate, an unreadable body): "could not find out" is
 * not "the server said off", and the hook keeps the two apart. Only a
 * successful answer stays in `pending` for the page's life; a failure
 * clears it, so the next caller asks again instead of inheriting the
 * failure until a reload. Limitation: callers that arrive while a request
 * is in flight share its outcome, success or not.
 */
let pending: Promise<HealthCapabilities> | null = null;
const fetchHealth = (): Promise<HealthCapabilities | null> => {
  const request = (pending ??= fetch(bobcatApiUrl('/ai/health'), {
    credentials: 'include',
  })
    .then((r) => {
      if (!r.ok) throw new Error(`/ai/health answered ${r.status}`);
      return r.json();
    })
    .then(readHealthCapabilities));
  return request.catch(() => {
    if (pending === request) pending = null;
    return null;
  });
};

/** The answer, or NONE where it could not be had (for callers that need a plain record). */
export const healthCapabilities = (): Promise<HealthCapabilities> =>
  fetchHealth().then((c) => c ?? NONE);

/** Test seam: forget the cached answer. */
export const resetHealthCapabilities = () => {
  pending = null;
};

/**
 * One capability from the shared answer: `undefined` until a SUCCESSFUL
 * answer arrives (and again after a failed request), `true`/`false` as the
 * server said. A caller that treats unknown as off says so itself.
 */
export const useHealthCapability = (
  key: keyof HealthCapabilities
): boolean | undefined => {
  const [ok, setOk] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let live = true;
    fetchHealth().then((c) => live && setOk(c ? c[key] : undefined));
    return () => {
      live = false;
    };
  }, [key]);
  return ok;
};

/**
 * Whether the "Call me" control is drawn: only on an explicit `true`
 * (unknown is not offered). A call already in flight keeps it on screen
 * whatever the deployment now says, because its Hang up button lives there
 * and `/voice/hang-up` is exempt from the switch.
 */
export const callMeOffered = (
  voiceCallsEnabled: boolean | undefined,
  callInFlight: boolean
): boolean => voiceCallsEnabled === true || callInFlight;
